import { Trans } from 'react-i18next';

import { HealthcareProviderElectronicDocumentIssuedNotificationEnabledSetCommand } from '@dreryk/egabinet-phone-notification-frontend-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { useFormController } from '@dreryk/react-common';
import {
   dispatchSnackbarPositiveEvent,
   IconFillColor,
   IconSize,
   IconWrapper,
   QuestionmarkCircleIcon,
   StyledFlex,
   Toggle,
   Tooltip,
} from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { patientPhoneNotificationTranslationKeys } from '../../translations';

import { PatientPhoneNotificationElectronicDocumentIssuedToggleLoader } from './PatientPhoneNotificationElectronicDocumentIssuedToggleLoader';
import { PhoneNotificationTooltipText } from './PhoneNotificationTooltipText';

const SAVED_SNACKBAR_DELAY_IN_MS = 5000;
const useCurrentHealthcareProviderElectronicDocumentIssuedNotificationEnabledSetCommand = (
   refetch: VoidFunction
): UseCqrsCommandResult<void, PatientPhoneNotificationElectronicDocumentIssuedToggleFormBean> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new HealthcareProviderElectronicDocumentIssuedNotificationEnabledSetCommand();
         command.enabled = Choice.of(parameters.notificationEnabled ?? false);
         return command;
      },
      {
         onSuccess: (ignore, variables) => {
            dispatchSnackbarPositiveEvent(
               <>
                  <Trans
                     i18nKey={
                        variables.notificationEnabled
                           ? patientPhoneNotificationTranslationKeys.notificationToggle
                                .electronicDocumentIssuedToggleSaveOn
                           : patientPhoneNotificationTranslationKeys.notificationToggle
                                .electronicDocumentIssuedToggleSaveOff
                     }
                  />
               </>,
               {
                  autoHideDelayInMs: SAVED_SNACKBAR_DELAY_IN_MS,
               }
            );
            refetch();
         },
      }
   );
};

type PatientPhoneNotificationElectronicDocumentIssuedToggleFormBean = {
   notificationEnabled: boolean;
};

export const PatientPhoneNotificationElectronicDocumentIssuedToggle = (props: {
   readonly notificationEnabled: boolean;
   readonly messages: string[];
   readonly refetch: VoidFunction;
}): JSX.Element | null => {
   const { register, handleSubmit } = useFormController<PatientPhoneNotificationElectronicDocumentIssuedToggleFormBean>(
      {
         notificationEnabled: props.notificationEnabled,
      },
      { mode: 'onChange' }
   );
   const {
      executeAsync: onSubmit,
      loading,
   } = useCurrentHealthcareProviderElectronicDocumentIssuedNotificationEnabledSetCommand(props.refetch);

   return (
      <StyledFlex gap={'12px'}>
         <form onInput={handleSubmit(onSubmit)}>
            <Toggle
               controller={register('notificationEnabled')}
               testId={'patientPhoneNotificationElectronicDocumentIssuedToggle'}
               disabled={loading}
            />
         </form>
         <StyledFlex gap={'4px'} alignItems={'center'}>
            <Trans
               i18nKey={patientPhoneNotificationTranslationKeys.notificationToggle.electronicDocumentIssuedToggle}
            />
            <Tooltip
               maxWidth={'500px'}
               testId={'electronicDocumentIssuedToggleTooltip'}
               content={
                  <PhoneNotificationTooltipText
                     header={
                        patientPhoneNotificationTranslationKeys.notificationToggle.electronicDocumentIssuedToggleTooltip
                     }
                     messages={props.messages}
                  />
               }
            >
               <IconWrapper
                  testId={'electronicDocumentIssuedToggleTooltipIconWrapper'}
                  fillColor={IconFillColor.PRIMARY}
                  size={IconSize.SMALL}
               >
                  <QuestionmarkCircleIcon />
               </IconWrapper>
            </Tooltip>
         </StyledFlex>
      </StyledFlex>
   );
};

export const PatientPhoneNotificationElectronicDocumentIssuedToggleWithLoader = (): JSX.Element => {
   return (
      <PatientPhoneNotificationElectronicDocumentIssuedToggleLoader>
         {({ notificationEnabled, messages, refetch }) => (
            <PatientPhoneNotificationElectronicDocumentIssuedToggle
               notificationEnabled={notificationEnabled}
               messages={messages}
               refetch={refetch}
            />
         )}
      </PatientPhoneNotificationElectronicDocumentIssuedToggleLoader>
   );
};
